import { ROLES } from '@/core/security/roles';
import { decodeTokenPayload } from '@/shared/services/auth/tokenPayload';
import type { FeaturePermissions, User } from '../models';

/**
 * A supplier's session -- the Supplier Bid Quotation portal (2026-10-23).
 *
 * A supplier signs in with a one-time code sent to the e-mail on its supplier-master record and holds no user
 * account, so there is no `GET /users/me` to read. Who it is travels in its access token instead: `user_type`
 * "supplier", its `supplier_id`, `email` and `name`, and the grants it holds (`fperms`, the Supplier role's on the
 * Supplier Bid feature only). Read here, decoded but never verified -- the client holds no key, and every endpoint
 * enforces the same claims.
 */

/** The access token's claims a supplier session carries. */
interface SupplierClaims {
  user_type?: unknown;
  supplier_id?: unknown;
  email?: unknown;
  name?: unknown;
  legal_entity_id?: unknown;
  fperms?: unknown;
}

export interface SupplierIdentity {
  supplierId: number;
  email: string;
  name: string;
  legalEntityId: number | null;
  /** `Feature:ACTION` pairs, as the token's `fperms` claim lists them. */
  featurePermissions: string[];
}

/** The supplier a token signs in, or null for a user's token or no token. */
export function supplierFromToken(token: string | null | undefined): SupplierIdentity | null {
  const payload = decodeTokenPayload(token);
  if (!payload || typeof payload !== 'object') return null;
  const claims = payload as SupplierClaims;
  if (claims.user_type !== 'supplier') return null;
  const supplierId = Number(claims.supplier_id);
  const email = typeof claims.email === 'string' ? claims.email.trim() : '';
  if (!Number.isFinite(supplierId) || email === '') return null;
  const name = typeof claims.name === 'string' && claims.name.trim() !== '' ? claims.name.trim() : email;
  const legalEntityId = Number(claims.legal_entity_id);
  const featurePermissions = Array.isArray(claims.fperms)
    ? claims.fperms.filter((value): value is string => typeof value === 'string')
    : [];
  return {
    supplierId,
    email,
    name,
    legalEntityId: Number.isFinite(legalEntityId) ? legalEntityId : null,
    featurePermissions,
  };
}

/** Whether a token is a supplier's. */
export function isSupplierToken(token: string | null | undefined): boolean {
  return supplierFromToken(token) !== null;
}

/** `["Supplier Bid:READ", ...]` as the grants `/users/me` would have reported, lower-cased actions. */
function grantsOf(pairs: readonly string[]): FeaturePermissions[] {
  const byFeature = new Map<string, Set<string>>();
  for (const pair of pairs) {
    const at = pair.lastIndexOf(':');
    if (at <= 0) continue;
    const feature = pair.slice(0, at);
    const action = pair.slice(at + 1).toLowerCase();
    const actions = byFeature.get(feature) ?? new Set<string>();
    actions.add(action);
    byFeature.set(feature, actions);
  }
  return [...byFeature].map(([feature, actions]) => ({ feature, permissions: [...actions] }));
}

/** The app's User for a supplier session, built from its token. */
export function supplierUser(identity: SupplierIdentity): User {
  return {
    id: `supplier:${String(identity.supplierId)}`,
    userName: identity.email,
    firstName: identity.name,
    lastName: '',
    emailAddress: identity.email,
    name: identity.name,
    email: identity.email,
    roles: [ROLES.SUPPLIER],
    roleName: 'Supplier',
    featuresAndPermissions: grantsOf(identity.featurePermissions),
    entityDetail: [],
  };
}
